// client/src/components/listaEspera/ListaEsperaPage.js
import React, { Fragment, useState, useCallback } from 'react';
import { Clock, PlusCircle, XCircle, Loader2, MoreVertical, CheckCircle2, Ban } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';
import { useClientesDaConta, useServicosDaConta } from '../../hooks/useVendaFormData';
import { useUnidades } from '../../hooks/useUnidades';
import {
    useListaEsperaEntradas,
    useCriarEntradaListaEspera,
    useCancelarEntradaListaEspera,
    useConverterEntradaListaEspera,
} from '../../hooks/useListaEspera';

const STATUS_LABEL = { aguardando: 'Aguardando', notificado: 'Vaga encontrada', convertido: 'Convertido', cancelado: 'Cancelado' };
const STATUS_STYLE = {
    aguardando: { background: '#f1f5f9', color: '#475569' },
    notificado: { background: '#fef3c7', color: '#92400e' },
    convertido: { background: '#dcfce7', color: '#166534' },
    cancelado: { background: '#fee2e2', color: '#991b1b' },
};

const StatusBadge = ({ status }) => (
    <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600, ...(STATUS_STYLE[status] || STATUS_STYLE.aguardando) }}>
        {STATUS_LABEL[status] || status}
    </span>
);

const nomeCliente = (cliente) => (typeof cliente === 'object' ? cliente?.user?.name || cliente?.nome : null) || 'Cliente';

const FORM_VAZIO = { clienteId: '', unidadeId: '', servicoId: '', dataDesejada: '', observacoes: '' };

const NovaEntradaForm = ({ onSalvar, onCancelar, isPending }) => {
    const [form, setForm] = useState(FORM_VAZIO);
    const { data: clientes = [] } = useClientesDaConta();
    const { data: servicos = [] } = useServicosDaConta();
    const { data: unidadesData } = useUnidades();
    const unidades = unidadesData?.data ?? [];

    const handleSubmit = (e) => {
        e.preventDefault();
        onSalvar(form);
    };

    return (
        <div className="content-panel">
            <form onSubmit={handleSubmit}>
                <div className="form-section">
                    <h4 className="form-section-title">Nova Entrada na Lista de Espera</h4>
                    <div className="form-grid-container-cols-2">
                        <div className="form-group">
                            <label className="form-label required">Cliente</label>
                            <select className="form-input" value={form.clienteId} onChange={(e) => setForm((p) => ({ ...p, clienteId: e.target.value }))} required>
                                <option value="">Selecione o cliente...</option>
                                {clientes.map((c) => (
                                    <option key={c._id} value={c._id}>{c.nome}{c.cpf ? ` (${c.cpf})` : ''}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label required">Unidade</label>
                            <select className="form-input" value={form.unidadeId} onChange={(e) => setForm((p) => ({ ...p, unidadeId: e.target.value }))} required>
                                <option value="">Selecione a unidade...</option>
                                {unidades.map((u) => (
                                    <option key={u._id} value={u._id}>{u.nome}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label required">Serviço</label>
                            <select className="form-input" value={form.servicoId} onChange={(e) => setForm((p) => ({ ...p, servicoId: e.target.value }))} required>
                                <option value="">Selecione o serviço...</option>
                                {servicos.map((s) => (
                                    <option key={s._id} value={s._id}>{s.nome}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label required">Data Desejada</label>
                            <input
                                type="date"
                                className="form-input"
                                value={form.dataDesejada}
                                onChange={(e) => setForm((p) => ({ ...p, dataDesejada: e.target.value }))}
                                required
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Observações</label>
                        <textarea
                            className="form-input"
                            rows={2}
                            value={form.observacoes}
                            onChange={(e) => setForm((p) => ({ ...p, observacoes: e.target.value }))}
                            placeholder="Ex: prefere período da manhã"
                        />
                    </div>
                </div>
                <div className="form-actions">
                    <button type="button" onClick={onCancelar} className="action-button action-button-secondary">
                        <XCircle size={18} className="icon" /> Cancelar
                    </button>
                    <button type="submit" className="action-button action-button-primary" disabled={isPending}>
                        {isPending ? 'Salvando...' : 'Adicionar à Lista'}
                    </button>
                </div>
            </form>
        </div>
    );
};

const ListaEsperaPage = () => {
    const [showForm, setShowForm] = useState(false);
    const [statusFiltro, setStatusFiltro] = useState('aguardando');
    const [selecionado, setSelecionado] = useState(null);

    const { data, isLoading, isError, error } = useListaEsperaEntradas({ status: statusFiltro || undefined, limit: 50 });
    const criarEntrada = useCriarEntradaListaEspera();
    const cancelarEntrada = useCancelarEntradaListaEspera();
    const converterEntrada = useConverterEntradaListaEspera();

    const entradas = data?.data || [];

    const handleSalvar = useCallback((form) => {
        criarEntrada.mutate(form, { onSuccess: () => setShowForm(false) });
    }, [criarEntrada]);

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Clock />}
                    title="Lista de Espera"
                    subtitle={!isLoading ? `${entradas.length} entrada${entradas.length !== 1 ? 's' : ''}` : null}
                    actions={showForm && (
                        <button onClick={() => setShowForm(false)} className="action-button action-button-secondary">
                            <XCircle size={18} className="icon" /> Fechar
                        </button>
                    )}
                />

                {!showForm && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
                        {['aguardando', 'notificado', 'convertido', 'cancelado', ''].map((s) => (
                            <button
                                key={s || 'todos'}
                                onClick={() => setStatusFiltro(s)}
                                className={`action-button ${statusFiltro === s ? 'action-button-primary' : 'action-button-secondary'}`}
                                style={{ fontSize: '0.8125rem' }}
                            >
                                {s ? STATUS_LABEL[s] : 'Todos'}
                            </button>
                        ))}
                    </div>
                )}

                {showForm && (
                    <NovaEntradaForm onSalvar={handleSalvar} onCancelar={() => setShowForm(false)} isPending={criarEntrada.isPending} />
                )}
            </div>

            {!showForm && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state"><Loader2 size={24} className="animate-spin" /><span>Carregando lista de espera...</span></div>
                        ) : isError ? (
                            <div className="table-empty-state">
                                <h3>Não foi possível carregar</h3>
                                <p>{error?.response?.data?.error || 'Erro ao carregar a lista de espera.'}</p>
                            </div>
                        ) : entradas.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Cliente</th>
                                        <th>Serviço</th>
                                        <th>Unidade</th>
                                        <th>Data Desejada</th>
                                        <th>Status</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entradas.map((entrada) => (
                                        <tr key={entrada._id}>
                                            <td>{nomeCliente(entrada.cliente)}</td>
                                            <td>{entrada.servico?.nome || '—'}</td>
                                            <td>{entrada.unidade?.nome || '—'}</td>
                                            <td>{new Date(entrada.dataDesejada).toLocaleDateString('pt-BR')}</td>
                                            <td><StatusBadge status={entrada.status} /></td>
                                            <td className="text-right">
                                                {['aguardando', 'notificado'].includes(entrada.status) && (
                                                    <button onClick={() => setSelecionado(entrada)} className="action-button-icon text-gray-600" title="Ações">
                                                        <MoreVertical size={18} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <Clock size={48} />
                                <h3>Nenhuma entrada encontrada</h3>
                                <p>{statusFiltro === 'aguardando' ? 'Ninguém esperando vaga no momento.' : 'Nenhuma entrada nesse status.'}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <FabButton onClick={() => setShowForm(true)} title="Nova Entrada" visible={!showForm} icon={<Clock size={26} className="icon" />} />

            <ActionsDrawer
                open={!!selecionado}
                onClose={() => setSelecionado(null)}
                title={selecionado ? nomeCliente(selecionado.cliente) : 'Entrada'}
                subtitle={selecionado?.servico?.nome}
                actions={selecionado ? [
                    {
                        key: 'converter',
                        label: 'Marcar Convertido (já agendei)',
                        icon: <CheckCircle2 size={20} className="icon" />,
                        onClick: () => { converterEntrada.mutate({ id: selecionado._id }); setSelecionado(null); },
                        loading: converterEntrada.isPending,
                    },
                    {
                        key: 'cancelar',
                        label: 'Cancelar Entrada',
                        icon: <Ban size={20} className="icon" />,
                        onClick: () => { cancelarEntrada.mutate(selecionado._id); setSelecionado(null); },
                        destructive: true,
                        loading: cancelarEntrada.isPending,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default ListaEsperaPage;
